import React, { useState, useRef, useEffect } from 'react';
import { FiGlobe, FiCheck } from 'react-icons/fi';
import { useTranslation } from '../hooks/useTranslation';

// Language flag emojis and additional info
const LANGUAGE_FLAGS = {
  en: { flag: '🇬🇧', label: 'EN' },
  de: { flag: '🇩🇪', label: 'DE' },
  es: { flag: '🇪🇸', label: 'ES' },
  fr: { flag: '🇫🇷', label: 'FR' },
  it: { flag: '🇮🇹', label: 'IT' },
  pt: { flag: '🇵🇹', label: 'PT' },
  nl: { flag: '🇳🇱', label: 'NL' },
  pl: { flag: '🇵🇱', label: 'PL' },
  ru: { flag: '🇷🇺', label: 'RU' },
  ja: { flag: '🇯🇵', label: 'JA' },
  zh: { flag: '🇨🇳', label: 'ZH' },
  ko: { flag: '🇰🇷', label: 'KO' }
};

const LanguageSwitcher = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { currentLanguage, changeLanguage, availableLanguages, isLoading } = useTranslation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = async (langCode) => {
    if (langCode !== currentLanguage) {
      await changeLanguage(langCode);
    }
    setIsOpen(false);
  };

  const currentLangInfo = LANGUAGE_FLAGS[currentLanguage] || { flag: '🌐', label: currentLanguage.toUpperCase() };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className="flex items-center space-x-1 p-2 text-text-light hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
        title="Change language"
      >
        <FiGlobe className="w-5 h-5" />
        <span className="text-sm font-medium hidden sm:inline">{currentLangInfo.label}</span>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50 border border-gray-200">
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Select Language
            </p>
          </div>

          <div className="py-1">
            {availableLanguages.map((lang) => {
              const langInfo = LANGUAGE_FLAGS[lang.code] || { flag: '🌐', label: lang.code.toUpperCase() };
              const isSelected = lang.code === currentLanguage;

              return (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`
                    flex items-center justify-between w-full px-4 py-2 text-sm transition-colors
                    ${isSelected
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">{langInfo.flag}</span>
                    <div className="text-left">
                      <p className="font-medium">{lang.nativeName}</p>
                      <p className="text-xs text-gray-500">{lang.name}</p>
                    </div>
                  </div>
                  {isSelected && (
                    <FiCheck className="w-4 h-4 text-primary-600" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
